/**
 * Where a stock-request `meta.errors` string should land on the submit form
 * (docs/STOCK_REQUEST_API.md, flow step 4). Shares the BFF multipart-cap wording with petty cash.
 */
export type StockRequestErrorTarget = 'inactive' | 'photoRequired' | 'photoTooLarge' | 'uploadFailed' | 'photo' | 'product' | 'quantity' | 'type' | 'generic';

export function classifyStockRequestError(msg: string): StockRequestErrorTarget {
    if (msg === 'POS user is inactive or locked') return 'inactive';
    if (msg.startsWith('proof image is required')) return 'photoRequired';
    if (msg.startsWith('proof must be at most') || msg.startsWith('image must be at most')) return 'photoTooLarge';
    if (msg.startsWith('Failed to upload proof image')) return 'uploadFailed';
    if (msg.startsWith('proof')) return 'photo';
    if (msg.startsWith('productPointId') || msg.startsWith('product does not belong')) return 'product';
    if (msg.startsWith('quantity')) return 'quantity';
    if (msg.startsWith('type')) return 'type';
    return 'generic';
}

/** Server quantities are raw stock units; the cashier sees `usageUnit`s. */
export const toDisplayQty = (raw: number, stockUsage: number) => raw * stockUsage;

/** The raw number in `quantity exceeds current stock (10)`, or null. */
export function rawFromMessage(msg: string): number | null {
    const m = /\(([\d.]+)\)\s*$/.exec(msg);
    return m ? Number(m[1]) : null;
}

/**
 * Downscale a photo to JPEG q0.8 until it fits `maxBytes`. Returns the original on any failure so the
 * caller's size guard (and the server's 1 MB check) still apply.
 */
export async function shrinkImage(file: File, maxBytes: number): Promise<File> {
    if (file.size <= maxBytes) return file;
    try {
        const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) return file;
        let out: Blob | null = null;
        // ponytail: fixed scale ladder; switch to a quality binary search if phone photos keep missing 1 MB
        for (const scale of [1, 0.75, 0.5, 0.35, 0.25]) {
            canvas.width = Math.max(1, Math.round(bmp.width * scale));
            canvas.height = Math.max(1, Math.round(bmp.height * scale));
            ctx.drawImage(bmp, 0, 0, canvas.width, canvas.height);
            out = await new Promise<Blob | null>((res) => canvas.toBlob(res, 'image/jpeg', 0.8));
            if (out && out.size <= maxBytes) break;
        }
        bmp.close();
        return out ? new File([out], 'proof.jpg', { type: 'image/jpeg' }) : file;
    } catch {
        return file;
    }
}
